import React, {ReactElement, useContext, useState} from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Divider} from '@mui/material';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import {ResourceType} from '../../../@types/resource-type';
import ResourceBase from '../../../@types/resource-base';
import OpenResource from '../modals/OpenResource.component';
import {ResourceAction} from '../../../@types/resource-action';
import GroupService from '../../../services/group.service';
import {toast} from 'react-toastify';
import DuplicateResource from '../modals/DuplicateResource.component';
import PublishComponent from '../modals/PublishComponent.component';
import componentService from '../../../services/component.service';
import {ComponentLifecycleTransition, ComponentState, ReleaseLevel} from '../../../@types/component';
import {excludeGroupAndDescendants} from '../../../utils/group-utils';
import {canEditComponentDraft} from '../../../utils/component-utils';
import {
    createWorkspaceObject,
    deleteResourceById,
    duplicateResourceById,
    fetchAllResources,
    filterOnGroupCanEdit,
    filterOnGroupCanEditResources,
    getSingularResourceLabel,
    getSubResourceOnDelete,
    getTreeItemDescendantIds
} from '../../../utils/resource-utils';
import DeleteResource from '../modals/DeleteResource.component';
import {UserContext} from '../../../context/userContext';
import {hasRightToEdit} from '../../../utils/role-to-rights';
import LeaveGroupModal from '../modals/LeaveGroup.component';
import EventSequenceSummary from '../../../@types/event-sequence-summary';
import {WorkspaceResource} from '../../../@types/workspace-types';
import {CanvasActionType} from '../../../@types/canvas-action-type';
import SanitizeModal from '../../form-components/SanitizeModal.component';
import {exportEventSequence, importEventSequence} from '../../../utils/event-sequence-utils';
import SafetyModel from '../../../@types/safety-model';
import {addEventSequence} from '../../../utils/safety-model-utils';
import {ExportEventSequenceSelectorDialogBox} from '../../form-components/ExportEventSequenceSelector.component';
import SafetyService from '../../../services/safety.service';
import Group from '../../../@types/group';
import authService from '../../../services/auth.service';
import serviceUserAccountService from '../../../services/service-user-account.service';
import ServiceUserAccount from '../../../@types/service-user-account';
import {addOrUpdateUser} from '../../../utils/service-user-account-utils';

export default function ResourceMenu(props: { label: string, addOrSelectResource: any, loadObjects: any }): ReactElement {
    const { currentWorkspaceObject,
        setCurrentWorkspaceObject,
        setCurrentAction,
        setCurrentCanvasAction,
        setDeletedWorkspaceObject,
        workspaceObjects,
        setWorkspaceObjects,
        getAdditionalResource,
        addOrUpdateAdditionalResource,
        publishResourceUpdate } = useWorkspaceContext();
    const { user } = useContext(UserContext);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openResource, setOpenResource] = React.useState<boolean>(false);
    const [duplicateResource, setDuplicateResource] = React.useState<boolean>(false);
    const [publishComponent, setPublishComponent] = React.useState<boolean>(false);
    const [publishing, setPublishing] = React.useState<boolean>(false);
    const [sanitizeResource, setSanitizeResource] = React.useState<boolean>(false);
    const [leaveGroup, setLeaveGroup] = React.useState<boolean>(false);
    const [deleteResource, setDeleteResource] = React.useState<boolean>(false);
    const [exportResource, setExportResource] = React.useState<boolean>(false);
    const [eventSequenceList, setEventSequenceList] = React.useState<EventSequenceSummary[]>([]);
    const open = Boolean(anchorEl);

    const loadData = (setDataReady: CallableFunction): void => {
        if (currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCES || currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_TREES) {
            let safetyModels = getAdditionalResource(ResourceType.SAFETY_MODELS);
            if (!safetyModels.resources || safetyModels.resources.length === 0) {
                fetchAllResources(ResourceType.SAFETY_MODELS)?.then((sms) => {
                    addOrUpdateAdditionalResource({resources: sms, objectType: ResourceType.SAFETY_MODELS});
                });
            }
        }
        let groups = getAdditionalResource(ResourceType.GROUPS);
        if (!groups.resources || groups.resources.length === 0) {
            fetchAllResources(ResourceType.GROUPS)?.then((grps) => {
                addOrUpdateAdditionalResource({resources: grps, objectType: ResourceType.GROUPS});
            }).finally(() => {
                setDataReady(true);
            });
        }
    };

    function onReissueApiKey(): void {
        if (!currentWorkspaceObject) return;
        serviceUserAccountService.reissueServiceUserKey(currentWorkspaceObject.object as ServiceUserAccount).then((updatedUser: ServiceUserAccount) => {

            if (workspaceObjects?.objectType === ResourceType.SERVICE_USER_ACCOUNTS) {
                setWorkspaceObjects({ objectType: ResourceType.SERVICE_USER_ACCOUNTS, resources: addOrUpdateUser(updatedUser, workspaceObjects.resources as ServiceUserAccount[]) });
            }
            toast.success('API key reissued successfully');
        }).catch((error) => {
            toast.error('Error reissuing API key - ' + error.message);
        });
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void =>  {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const onClickOpen = (): void =>  {
        setOpenResource(true);
        handleClose();
    }

    const handleOpenClose = (): void => {
        setOpenResource(false);
    }

    const handleOpenResource = (resource: ResourceBase): void => {
        resource.type = currentWorkspaceObject ? currentWorkspaceObject.objectType : workspaceObjects?.objectType ?? ResourceType.GROUPS;
        props.addOrSelectResource(resource);
        setOpenResource(false);
    }
    const onClickNew = (): void => {
        props.addOrSelectResource(new ResourceBase(currentWorkspaceObject ? currentWorkspaceObject.objectType : workspaceObjects?.objectType ?? ResourceType.GROUPS));
        handleClose();
    };

    const onClickSave = (): void => {
        setCurrentAction(ResourceAction.SAVE);
        handleClose();
    }

    const onClickDuplicate = (): void =>  {
        setDuplicateResource(true);
        handleClose();
    }

    // The same ways in as the explorer's Properties, Edit/View and Mapping actions: each view is its
    // own tab, keyed by the component, selected rather than opened again when it is already open.
    const openComponentView = (viewType: ResourceType): void => {
        const componentId = currentWorkspaceObject?.object?.id;
        if (componentId) {
            props.addOrSelectResource(new ResourceBase(viewType, componentId));
        }
        handleClose();
    }

    const onClickPublish = (): void => {
        setPublishComponent(true);
        handleClose();
    }

    const onConfirmPublish = (releaseLevel: ReleaseLevel, releaseNotes: string): void => {
        const componentId = currentWorkspaceObject?.object?.id;
        if (!componentId) {
            return;
        }
        setPublishing(true);
        componentService.applyLifecycleTransition(componentId, ComponentLifecycleTransition.PUBLISH,
            releaseLevel, releaseNotes)
            .then((saved) => {
                publishResourceUpdate({
                    type: ResourceType.COMPONENT,
                    id: saved.id,
                    updatedData: saved,
                    source: 'save',
                });
                toast.success(`Published v${saved.version} of ${saved.name}`);
                setPublishComponent(false);
            })
            .catch((error) => {
                toast.error('Error publishing component - ' + error.message);
            })
            .finally(() => setPublishing(false));
    }

    const onClickSanitize = (): void =>  {
        setSanitizeResource(true);
        handleClose();
    }

    const onClickExport = (): void => {
        if (currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCES || currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_TREES) {
            exportEventSequence(currentWorkspaceObject?.object);
        } else if (currentWorkspaceObject?.objectType === ResourceType.SAFETY_MODELS) {
            loadEventSequences(currentWorkspaceObject.object);
            setExportResource(true);
        }
        handleClose();
    }

    const onClickDelete = (): void =>  {
        setDeleteResource(true);
        handleClose();
    }

    // Mirrors the explorer's component delete, so either view (details or canvas) closes every tab
    // on the component and the explorer drops its row.
    const deleteComponent = (): void => {
        const componentId = currentWorkspaceObject?.object?.id;
        const componentName = getObjectDisplayName();
        if (!componentId) {
            setDeleteResource(false);
            return;
        }
        // Only DRAFT components can be deleted; the server enforces this and answers 400 otherwise.
        componentService.deleteComponent(componentId)
            .then(() => {
                setCurrentWorkspaceObject(null);
                setDeletedWorkspaceObject({objectType: ResourceType.COMPONENT, id: componentId});
                publishResourceUpdate({
                    type: ResourceType.COMPONENT,
                    id: componentId.toString(),
                    source: 'delete',
                    updatedData: null,
                });
                setWorkspaceObjects({objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS});
                toast.success(`"${componentName}" deleted successfully`);
            })
            .catch((error) => {
                toast.error('Error deleting component - ' + error.message);
            })
            .finally(() => setDeleteResource(false));
    }

    const onClickLeave = (): void =>  {
        setLeaveGroup(true);
        handleClose();
    }
    
    const onClickClose = (): void => {
        setCurrentAction(ResourceAction.CLOSE);
        handleClose();
    }

    const onClickImport = (): void => {
        handleClose();
        let safetyModel = getSafetyModel();
        if (safetyModel) {
            importEventSequence(safetyModel.id, safetyModel.name, (eventSequence: any) => {
                addEventSequence(safetyModel, eventSequence);
                if (workspaceObjects?.objectType === ResourceType.SAFETY_MODELS) {
                    setWorkspaceObjects({objectType: ResourceType.SAFETY_MODELS});
                }
            });
        }
    }

    const onClickUndo = (): void => {
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_UNDO });
        handleClose();
    }

    const onClickRedo = (): void => {
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_REDO });
        handleClose();
    }

    const onClickHistory = (): void => {
        setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_HISTORY, object: currentWorkspaceObject?.object });
        handleClose();
    }

    const onClickViewModel = (): void => {
        // eslint-disable-next-line no-console
        // console.log('ResourceMenu: onClickViewModel called for:', currentWorkspaceObject?.object);
        // eslint-disable-next-line no-console
        // console.log('ResourceMenu: Stack trace:', new Error().stack);
        setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: currentWorkspaceObject?.object });
        handleClose();
    }

    const isResourceNotSavable = (): boolean => {
        if (currentWorkspaceObject?.object?.group) {
            let group = new Group();
            Object.assign(group, currentWorkspaceObject.object.group);
            if (currentWorkspaceObject.object) {
                currentWorkspaceObject.object.group = group;
            }
        }
        return currentWorkspaceObject === null || currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_HISTORY ||
            currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_HISTORY_TREES ||
            (currentWorkspaceObject?.object?.id && !hasRightToEdit(currentWorkspaceObject, user?.superUser));
    }

    const isResourceNotEditable = (): boolean => {
        return currentWorkspaceObject === null || !currentWorkspaceObject.object?.id || currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_HISTORY ||
            currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_HISTORY_TREES || !hasRightToEdit(currentWorkspaceObject, user?.superUser);
    }

    const handleClickNewInDuplicate = (resource: ResourceBase): void => {
        setCurrentWorkspaceObject({objectType: resource.type, object: createWorkspaceObject(resource) });
        setDuplicateResource(false);
    };

    const generateDuplicateData = (): WorkspaceResource => {
        if (currentWorkspaceObject?.objectType === ResourceType.GROUPS) {
            return excludeGroupAndDescendants(getAdditionalResource(ResourceType.GROUPS), currentWorkspaceObject.object, workspaceObjects);
        } else if (currentWorkspaceObject?.objectType === ResourceType.SAFETY_MODELS ||
            currentWorkspaceObject?.objectType === ResourceType.OCCURRENCE_REPORTS) {
            return filterOnGroupCanEditResources(getAdditionalResource(ResourceType.GROUPS));
        } else if (currentWorkspaceObject?.objectType === ResourceType.PERFORMANCE_SHAPING_FACTORS) {
            return filterOnGroupCanEdit(getAdditionalResource(ResourceType.GROUPS));
        } else {
            return filterOnGroupCanEditResources(getAdditionalResource(ResourceType.SAFETY_MODELS));
        }
    }

    const getSafetyModel = (): SafetyModel|undefined => {
        if (currentWorkspaceObject?.objectType === ResourceType.SAFETY_MODELS) {
            return currentWorkspaceObject.object;
        } else if (currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCES || currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_TREES) {
            let safetyModels = getAdditionalResource(ResourceType.SAFETY_MODELS).resources as SafetyModel[];
            return safetyModels.find((sm) => sm.id == currentWorkspaceObject.object?.containerId);
        }
    }

    const loadEventSequences = (safetyModel: SafetyModel): void => {
        if (safetyModel.numberEventSequences > 0 && !safetyModel.children) {
            SafetyService.getEventSequences(safetyModel.id).then((eventSequences) => {
                (eventSequences as EventSequenceSummary[])?.forEach(es => es.role = safetyModel.group.role());
                setEventSequenceList(eventSequences);
            }).catch((error) => {
                toast.error('Error getting event sequences - ' + error.message);
            });
        } else {
            setEventSequenceList(safetyModel.children ?? []);
        }
    }

    // The details form, the fault tree canvas and the mapping view all hold the Component, so each
    // offers the same component actions as the explorer.
    const isComponentView: boolean = currentWorkspaceObject?.objectType === ResourceType.COMPONENT
        || currentWorkspaceObject?.objectType === ResourceType.COMPONENT_TREE
        || currentWorkspaceObject?.objectType === ResourceType.COMPONENT_MAPPING
        || currentWorkspaceObject?.objectType === ResourceType.COMPONENT_HISTORY;
    const isComponentCanvas: boolean = currentWorkspaceObject?.objectType === ResourceType.COMPONENT_TREE;
    // Undo and redo work on a draft's history; a published release is read-only.
    const canUndoRedoComponent: boolean = isComponentCanvas && canEditComponentDraft(currentWorkspaceObject?.object,
        (getAdditionalResource(ResourceType.GROUPS).resources as Group[]) ?? []);

    const getObjectDisplayName = (): string => {
        let displayName = currentWorkspaceObject?.object?.name;
        if (currentWorkspaceObject && currentWorkspaceObject.object && currentWorkspaceObject.object.displayName) {
            displayName =  currentWorkspaceObject.object.displayName;
        }
        return displayName;
    }
 
    // @ts-ignore
    return (
        <>
            <Button
                id="resource-menu-button"
                aria-controls={open ? 'resource-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={anchorEl ? 'true' : undefined}
                aria-label="Resource Menu"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon/>}
                style={{ color: 'white', textTransform: 'none', }}
                sx={{
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                }}                
            >
                {props.label}
            </Button> 
            <Menu
                id="resource-menu"
                aria-labelledby="resource-menu-button"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'resource-menu-button',
                    }
                }}  
            >
                <MenuItem
                    onClick={onClickNew}
                    disabled={currentWorkspaceObject?.objectType.includes('eventSequence') || 
                        currentWorkspaceObject?.objectType === ResourceType.NEW_OCCURRENCE_REPORTS ||
                        ((currentWorkspaceObject?.objectType === ResourceType.GROUPS ||
                        (workspaceObjects?.objectType === ResourceType.GROUPS && currentWorkspaceObject?.object === undefined)) && !authService.isSuperUser())}
                    dense
                >
                    New
                </MenuItem>

                <Divider />

                {currentWorkspaceObject?.objectType !== ResourceType.SERVICE_USER_ACCOUNTS && 
                    <MenuItem onClick={onClickOpen}
                        disabled={currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_HISTORY_TREES ||
                        currentWorkspaceObject?.objectType === ResourceType.NEW_OCCURRENCE_REPORTS}
                        dense>Open</MenuItem>
                }
                {currentWorkspaceObject?.objectType === ResourceType.SERVICE_USER_ACCOUNTS && 
                    <MenuItem onClick={onReissueApiKey}  dense>Reissue API Key</MenuItem>
                }
                {currentWorkspaceObject?.objectType !== ResourceType.OCCURRENCE_REPORTS &&currentWorkspaceObject?.objectType !== ResourceType.SERVICE_USER_ACCOUNTS &&
                    <MenuItem
                        onClick={onClickSave}
                        disabled={isResourceNotSavable() || currentWorkspaceObject?.objectType === ResourceType.NEW_OCCURRENCE_REPORTS ||
                        (currentWorkspaceObject?.objectType === ResourceType.GROUPS && !authService.isSuperUser())}
                        dense
                    >
                        Save
                    </MenuItem>
                }
                {/* No Duplicate for a component: there is no component duplicate flow, so the explorer omits it too. */}
                {currentWorkspaceObject?.objectType !== ResourceType.SERVICE_USER_ACCOUNTS && currentWorkspaceObject?.objectType !== ResourceType.OCCURRENCE_REPORT_DESCRIPTORS && !isComponentView && <MenuItem
                    onClick={onClickDuplicate}
                    disabled={currentWorkspaceObject == undefined ||
                    currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_HISTORY_TREES ||
                    currentWorkspaceObject.objectType === ResourceType.USERS ||
                    currentWorkspaceObject.objectType === ResourceType.NEW_OCCURRENCE_REPORTS ||
                    (currentWorkspaceObject?.objectType === ResourceType.GROUPS && !authService.isSuperUser())}
                    dense
                >
                    Duplicate
                </MenuItem>
                }
                {(currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCES || currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_HISTORY) &&
                    <MenuItem onClick={onClickViewModel} disabled={ currentWorkspaceObject == undefined } dense>View Model Tree</MenuItem>
                }
                {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES &&
                    <MenuItem
                        onClick={onClickSanitize}
                        disabled={isResourceNotEditable()}
                        dense
                    >
                        Sanitize
                    </MenuItem>
                }
                {currentWorkspaceObject && currentWorkspaceObject.objectType !== ResourceType.EVENT_SEQUENCE_HISTORY_TREES &&
                    currentWorkspaceObject.objectType !== ResourceType.OCCURRENCE_REPORTS &&
                    currentWorkspaceObject.objectType !== ResourceType.USERS &&
                    currentWorkspaceObject.objectType !== ResourceType.SERVICE_USER_ACCOUNTS &&
                    currentWorkspaceObject.objectType !== ResourceType.NEW_OCCURRENCE_REPORTS &&
                    currentWorkspaceObject.objectType !== ResourceType.OCCURRENCE_REPORT_DESCRIPTORS &&
                    currentWorkspaceObject.objectType !== ResourceType.PERFORMANCE_SHAPING_FACTORS && <Divider />}
                {((currentWorkspaceObject?.objectType == ResourceType.SAFETY_MODELS && currentWorkspaceObject.object?.numberEventSequences > 0) ||
                    currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCES || currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_HISTORY || currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES) &&
                    <MenuItem onClick={onClickExport} dense>Export Safety View</MenuItem>
                }
                {(currentWorkspaceObject?.objectType == ResourceType.SAFETY_MODELS || currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCES
                        || currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_HISTORY || currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES) &&
                    <MenuItem onClick={onClickImport} dense>Import Safety View</MenuItem>
                }
                {currentWorkspaceObject?.objectType == ResourceType.GROUPS &&
                    <MenuItem onClick={onClickLeave} disabled={ currentWorkspaceObject == undefined || authService.isSuperUser() } dense>Leave Working Area</MenuItem>
                }
                {isComponentView &&
                    <MenuItem
                        onClick={(): void => openComponentView(ResourceType.COMPONENT)}
                        disabled={!currentWorkspaceObject?.object?.id}
                        dense
                    >
                        Properties
                    </MenuItem>
                }
                {isComponentView &&
                    <MenuItem
                        onClick={(): void => openComponentView(ResourceType.COMPONENT_TREE)}
                        disabled={!currentWorkspaceObject?.object?.id}
                        dense
                    >
                        {/* A published release is frozen, so opening its canvas is a look, not an edit. */}
                        {currentWorkspaceObject?.object?.state === ComponentState.DRAFT ? 'Edit' : 'View'}
                    </MenuItem>
                }
                {isComponentView &&
                    <MenuItem
                        onClick={(): void => openComponentView(ResourceType.COMPONENT_MAPPING)}
                        disabled={!currentWorkspaceObject?.object?.id}
                        dense
                    >
                        Mapping
                    </MenuItem>
                }
                {isComponentView &&
                    <MenuItem
                        onClick={(): void => openComponentView(ResourceType.COMPONENT_HISTORY)}
                        disabled={!currentWorkspaceObject?.object?.id}
                        dense
                    >
                        History
                    </MenuItem>
                }
                {isComponentView &&
                    <MenuItem
                        onClick={onClickPublish}
                        disabled={currentWorkspaceObject?.object?.state !== ComponentState.DRAFT || !currentWorkspaceObject?.object?.id}
                        dense
                    >
                        Publish
                    </MenuItem>
                }
                <Divider />
                <MenuItem
                    onClick={onClickDelete}
                    disabled={isResourceNotEditable() || currentWorkspaceObject?.objectType === ResourceType.NEW_OCCURRENCE_REPORTS ||
                        (currentWorkspaceObject?.objectType === ResourceType.GROUPS && !authService.isSuperUser()) ||
                        (isComponentView && currentWorkspaceObject?.object?.state !== ComponentState.DRAFT)}
                    dense
                >
                    Delete
                </MenuItem>
                <Divider />
                {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && <MenuItem disabled={!hasRightToEdit(currentWorkspaceObject, user?.superUser)} onClick={onClickUndo} dense>Undo</MenuItem>}
                {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && <MenuItem disabled={!hasRightToEdit(currentWorkspaceObject, user?.superUser)} onClick={onClickRedo} dense>Redo</MenuItem>}
                {isComponentCanvas && <MenuItem disabled={!canUndoRedoComponent} onClick={onClickUndo} dense>Undo</MenuItem>}
                {isComponentCanvas && <MenuItem disabled={!canUndoRedoComponent} onClick={onClickRedo} dense>Redo</MenuItem>}
                {(currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCES || currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES) &&
                    <MenuItem onClick={onClickHistory} dense>History</MenuItem>}
                <MenuItem onClick={onClickClose} disabled={ currentWorkspaceObject == undefined } dense>Close</MenuItem>
            </Menu>
            <OpenResource open={openResource} onClose={handleOpenClose} addOrSelectResource={handleOpenResource} />
            {currentWorkspaceObject && currentWorkspaceObject.objectType !== ResourceType.USERS &&
                <DuplicateResource
                    open={duplicateResource}
                    title={'Duplicate ' + getSingularResourceLabel(currentWorkspaceObject.objectType) + ' - ' + getObjectDisplayName()}
                    onClose={(): void => { setDuplicateResource(false); }}
                    onConfirm={
                        (id: number): void => {
                            let targetId = id > 0 ? id : undefined;
                            if (currentWorkspaceObject) {
                                let sourceGroupId = currentWorkspaceObject.object?.id;
                                let duplicateResourcePromise = duplicateResourceById(currentWorkspaceObject.objectType, sourceGroupId!, targetId);
                                if (duplicateResourcePromise) {
                                    setDuplicateResource(false);
                                    duplicateResourcePromise.then((response) => {
                                        if (workspaceObjects?.objectType === currentWorkspaceObject.objectType) {
                                            setWorkspaceObjects({objectType: currentWorkspaceObject.objectType});
                                        }
                                        toast.info((response.name ?? (response.data ? (response.data.name ? response.data.name : response.data.displayName) : ''))
                                            + ' duplicated successfully.');
                                    }).catch((error) => {
                                        toast.error(error.message);
                                    });
                                }
                            }
                        }
                    }
                    object={currentWorkspaceObject}
                    data={generateDuplicateData()}
                    onClickNew={handleClickNewInDuplicate}
                    loadData={loadData}
                />
            }
            {isComponentView &&
                <PublishComponent
                    open={publishComponent}
                    componentName={getObjectDisplayName()}
                    publishing={publishing}
                    onClose={(): void => {
                        if (!publishing) {
                            setPublishComponent(false);
                        }
                    }}
                    onConfirm={onConfirmPublish}
                />
            }
            {currentWorkspaceObject && hasRightToEdit(currentWorkspaceObject, user?.superUser) &&
                <SanitizeModal
                    open={sanitizeResource}
                    title={'Sanitize ' + getSingularResourceLabel(ResourceType.EVENT_SEQUENCES) + ' - ' + currentWorkspaceObject.object?.name}
                    onClose={(): void => { setSanitizeResource(false); }}
                    onConfirm={
                        (sanitizeQtt: boolean, sanitizeODna: boolean): void => {
                            if (currentWorkspaceObject) {
                                // let the canvas deal with the update
                                // so that all canvas actions are in one place
                                if (sanitizeODna) {
                                    setCurrentCanvasAction({ actionType: CanvasActionType.SANITIZE_OCCURRENCE_DNA });
                                }
                                if (sanitizeQtt) {
                                    setCurrentCanvasAction({ actionType: CanvasActionType.SANITIZE_EVENT_SEQUENCE });
                                }
                            }
                        }
                    }
                    object={currentWorkspaceObject}

                />
            }
            {currentWorkspaceObject && hasRightToEdit(currentWorkspaceObject, user?.superUser) &&
                <DeleteResource
                    open={deleteResource}
                    resourceType={currentWorkspaceObject.objectType}
                    resourceName={currentWorkspaceObject.object?.name ?? currentWorkspaceObject.object?.displayName}
                    subResources={getSubResourceOnDelete(currentWorkspaceObject.objectType)}
                    handleDelete={(): void => {
                        if (isComponentView) {
                            deleteComponent();
                            return;
                        }
                        let deleteResourcePromise = deleteResourceById(currentWorkspaceObject?.objectType, currentWorkspaceObject.object?.id);
                        if (deleteResourcePromise) {
                            deleteResourcePromise.then(() => {
                                let childrenIds;
                                if (currentWorkspaceObject?.objectType === ResourceType.GROUPS) {
                                    childrenIds = getTreeItemDescendantIds(currentWorkspaceObject?.object,
                                        ResourceType.GROUPS,
                                        workspaceObjects?.resources ?? []);
                                } else {
                                    childrenIds = currentWorkspaceObject.object?.children?.map((child: EventSequenceSummary) => child.id) ?? [];
                                }
                                setDeletedWorkspaceObject({
                                    objectType: currentWorkspaceObject?.objectType,
                                    id: currentWorkspaceObject.object?.id!,
                                    children: childrenIds
                                });
                                setWorkspaceObjects({objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS});
                            }).catch((error) => {
                                toast.error(error.message);
                            }).finally(() => {
                                setDeleteResource(false);
                            });
                        }
                    }}
                    onClose={(): void => {
                        setDeleteResource(false);
                    }}
                />
            }
            {currentWorkspaceObject && currentWorkspaceObject.objectType === ResourceType.GROUPS &&
                <LeaveGroupModal
                    leaveGroup={leaveGroup}
                    group={currentWorkspaceObject.object}
                    onConfirm={(): void => {
                        setLeaveGroup(false);
                        let group = currentWorkspaceObject?.object;
                        let childrenIds = getTreeItemDescendantIds(group,
                            ResourceType.GROUPS,
                            workspaceObjects?.resources ?? []);
                        GroupService.leaveGroupById(group).then(() => {
                            setDeletedWorkspaceObject({
                                objectType: ResourceType.GROUPS,
                                id: group.id!,
                                children: childrenIds
                            });
                            setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS });
                        }).catch((error) => {
                            toast.error(error.message);
                        }
                        );
                    }}
                    onClose={(): void => {
                        setLeaveGroup(false);
                    }}
                />
            }
            {currentWorkspaceObject && currentWorkspaceObject.objectType === ResourceType.SAFETY_MODELS &&
                <ExportEventSequenceSelectorDialogBox
                    open={exportResource}
                    safetyModel={currentWorkspaceObject.object}
                    eventSequenceList={eventSequenceList}
                    onClose={(): void => { setExportResource(false); }}
                    onExport={(eventSequenceSelected: EventSequenceSummary | undefined): void => {
                        setExportResource(false);
                        if (!eventSequenceSelected) {
                            return;
                        }
                        exportEventSequence(eventSequenceSelected);
                    }}
                />
            }
        </>
    );
}